import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'
import type { StageId } from '@/types'

export interface AppearanceWithDetails {
  id: string
  guest_id: string
  episode_id: string
  stage: StageId
  ready_to_record: boolean
  declined: boolean
  note: string | null
  created_at: string
  updated_at: string
  // Joined guest data
  guest: {
    id: string
    name: string
    title: string
    company: string | null
    avatar_url: string | null
    email: string | null
  }
  // Joined episode data
  episode: {
    id: string
    number: number
    title: string
    recording_date: string
    recording_time: string | null
    air_date: string
    status: string
    series_id: string
  }
}

export interface CreateAppearanceInput {
  guest_id: string
  episode_id: string
  recording_date?: string
  recording_time?: string
  note?: string
}

export interface UpdateAppearanceInput {
  id: string
  stage?: StageId
  ready_to_record?: boolean
  declined?: boolean
  note?: string
}

/**
 * Hook to fetch appearances for an episode
 */
export function useEpisodeAppearances(episodeId: string | undefined) {
  const supabase = createClient()

  return useQuery({
    queryKey: ['appearances', 'episode', episodeId],
    queryFn: async (): Promise<AppearanceWithDetails[]> => {
      if (!episodeId) return []

      const { data, error } = await supabase
        .from('appearances')
        .select(`
          *,
          guest:guests(id, name, title, company, avatar_url, email),
          episode:episodes(id, number, title, recording_date, recording_time, air_date, status, series_id)
        `)
        .eq('episode_id', episodeId)
        .order('created_at', { ascending: true })

      if (error) throw error
      return data as unknown as AppearanceWithDetails[]
    },
    enabled: !!episodeId,
  })
}

/**
 * Hook to fetch appearances for a guest
 */
export function useGuestAppearances(guestId: string | undefined) {
  const supabase = createClient()

  return useQuery({
    queryKey: ['appearances', 'guest', guestId],
    queryFn: async (): Promise<AppearanceWithDetails[]> => {
      if (!guestId) return []

      const { data, error } = await supabase
        .from('appearances')
        .select(`
          *,
          guest:guests(id, name, title, company, avatar_url, email),
          episode:episodes(id, number, title, recording_date, recording_time, air_date, status, series_id)
        `)
        .eq('guest_id', guestId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as unknown as AppearanceWithDetails[]
    },
    enabled: !!guestId,
  })
}

/**
 * Hook to fetch all appearances for the current team
 */
export function useTeamAppearances() {
  const supabase = createClient()
  const { membership } = useAuth()

  return useQuery({
    queryKey: ['appearances', 'team', membership?.team_id],
    queryFn: async (): Promise<AppearanceWithDetails[]> => {
      if (!membership?.team_id) return []

      // First get all episodes for the team
      const { data: episodes, error: episodesError } = await supabase
        .from('episodes')
        .select('id')
        .eq('team_id', membership.team_id)

      if (episodesError) throw episodesError

      const episodeIds = (episodes as { id: string }[]).map((e) => e.id)
      if (episodeIds.length === 0) return []

      const { data, error } = await supabase
        .from('appearances')
        .select(`
          *,
          guest:guests(id, name, title, company, avatar_url, email),
          episode:episodes(id, number, title, recording_date, recording_time, air_date, status, series_id)
        `)
        .in('episode_id', episodeIds)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as unknown as AppearanceWithDetails[]
    },
    enabled: !!membership?.team_id,
  })
}

/**
 * Hook to create a new appearance (add guest to episode)
 */
export function useCreateAppearance() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateAppearanceInput) => {
      // If a custom recording date is provided, update the episode first
      if (input.recording_date) {
        const updateData: Record<string, string | null> = {
          recording_date: input.recording_date,
        }
        if (input.recording_time !== undefined) {
          updateData.recording_time = input.recording_time || null
        }

        const { error: episodeError } = await supabase
          .from('episodes')
          .update(updateData as never)
          .eq('id', input.episode_id)

        if (episodeError) throw episodeError
      }

      // Create the appearance
      const { data, error } = await supabase
        .from('appearances')
        .insert({
          guest_id: input.guest_id,
          episode_id: input.episode_id,
          stage: 'invited',
          note: input.note || null,
        } as never)
        .select(`
          *,
          guest:guests(id, name, title, company, avatar_url, email),
          episode:episodes(id, number, title, recording_date, recording_time, air_date, status, series_id)
        `)
        .single()

      if (error) throw error
      return data as unknown as AppearanceWithDetails
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['appearances'] })
      queryClient.invalidateQueries({ queryKey: ['episodes', data.episode.series_id] })
      queryClient.invalidateQueries({ queryKey: ['guests'] })
    },
  })
}

/**
 * Hook to update an appearance
 */
export function useUpdateAppearance() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateAppearanceInput) => {
      const { data, error } = await supabase
        .from('appearances')
        .update(input as never)
        .eq('id', id)
        .select(`
          *,
          guest:guests(id, name, title, company, avatar_url, email),
          episode:episodes(id, number, title, recording_date, recording_time, air_date, status, series_id)
        `)
        .single()

      if (error) throw error
      return data as unknown as AppearanceWithDetails
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appearances'] })
    },
  })
}

/**
 * Hook to delete an appearance (remove guest from episode)
 */
export function useDeleteAppearance() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (appearanceId: string) => {
      const { error } = await supabase
        .from('appearances')
        .delete()
        .eq('id', appearanceId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appearances'] })
      queryClient.invalidateQueries({ queryKey: ['guests'] })
    },
  })
}

/**
 * Hook to update episode recording date/time directly
 */
export function useUpdateEpisodeSchedule() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      episodeId,
      recordingDate,
      recordingTime,
    }: {
      episodeId: string
      recordingDate: string
      recordingTime?: string
    }) => {
      const { data, error } = await supabase
        .from('episodes')
        .update({
          recording_date: recordingDate,
          recording_time: recordingTime || null,
        } as never)
        .eq('id', episodeId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['episodes'] })
      queryClient.invalidateQueries({ queryKey: ['appearances'] })
    },
  })
}
